import React from 'react';

/**
 * Format currency values for display
 */
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

// Default cash-like asset symbols (money market funds and sweep vehicles)
const DEFAULT_CASH_LIKE_SYMBOLS = ['FCASH', 'SPAXX', 'SWVXX', 'VMFXX', 'FDRXX', 'SPRXX', 'FZFXX', 'SNSXX', 'SNVXX'];

/**
 * CashSummaryCard - Displays cash summary with Before/After comparison
 * Shows: Reserved Cash Target, Total Cash, Cash-like Assets
 * 
 * Props:
 *   accountAllocations - Array of account allocation objects
 *   allAccountHoldings - Object mapping account numbers to their holdings arrays
 *   trades - Array of trade objects (to calculate after values)
 *   cashLikeSymbols - Array of symbols considered cash-like (money market funds)
 */
const CashSummaryCard = React.memo(({ accountAllocations = [], allAccountHoldings = {}, trades = [], cashLikeSymbols, isLoading = false, ignoreMargin = false }) => {

    // Use provided symbols or fallback to defaults
    const CASH_LIKE_SYMBOLS = cashLikeSymbols || DEFAULT_CASH_LIKE_SYMBOLS;

    /**
     * Check if a symbol is a cash-like asset (money market fund, etc.)
     */
    const isCashLikeSymbol = (symbol) => {
        if (!symbol) return false;
        const upperSymbol = symbol.toUpperCase();
        return CASH_LIKE_SYMBOLS.map(s => s.toUpperCase()).includes(upperSymbol);
    };

    // Build a map of net trade impacts by account
    // SELL trades bring cash IN, BUY trades take cash OUT
    const netTradeImpactByAccount = {};
    (trades || []).forEach(trade => {
        const accountNumber = trade.account_number || trade.accountNumber;
        const action = (trade.action || trade.Action || '').toUpperCase();
        // Use abs value so both backend unsigned trade_value and live signed trade_value work
        const tradeValue = Math.abs(trade.trade_value || trade.value || 0);
        
        if (accountNumber) {
            const cashImpact = action === 'SELL' ? tradeValue : (action === 'BUY' ? -tradeValue : 0);
            netTradeImpactByAccount[accountNumber] = (netTradeImpactByAccount[accountNumber] || 0) + cashImpact;
        }
    });

    // Calculate cash values from account allocations
    // Note: Backend's cashCurrent includes BOTH pure cash AND cash-like assets (money market funds)
    // We need to separate them to avoid double counting
    let totalCashBefore = 0;
    let totalCashAfter = 0;
    let totalCashReserveTarget = 0;

    // Build account-level info for cash
    const accountCashInfo = {};

    accountAllocations.forEach(account => {
        const accountNumber = account.accountNumber || account.account_number;
        const explicitMargin = account.marginCashUsed ?? account.margin_cash_used ?? 0;
        const rawCash = account.cashCurrent ?? account.cash_current ?? 0;

        // The daily-rebalancer 'with_margin' path used to store cashCurrent as
        // _get_effective_cash() = Cash Current Value − Margin Cash Used.  But
        // Cash Current Value from Tamarac is ALREADY the net cash (negative for
        // margin accounts), so that subtracted margin twice.  Detect and undo the
        // double-subtraction: if rawCash < −margin the value is stale/doubled.
        const cashCurrent = (explicitMargin > 0 && rawCash < -explicitMargin)
            ? rawCash + explicitMargin   // undo double-subtraction → Tamarac net cash
            : rawCash;

        const cashReserveCenter = account.cashReserveCenter || account.cash_reserve_center || 0;
        
        // Calculate cashAfter from trades if not provided
        const tradeImpact = netTradeImpactByAccount[accountNumber] || 0;
        const cashAfter = cashCurrent + tradeImpact;

        totalCashBefore += cashCurrent;
        totalCashAfter += cashAfter;
        totalCashReserveTarget += cashReserveCenter;
        
        accountCashInfo[accountNumber] = {
            cashBefore: cashCurrent,
            cashAfter: cashAfter,
            cashChange: tradeImpact
        };
    });

    // Calculate cash-like assets from actual account holdings
    // These are money market funds identified by their symbol
    // IMPORTANT: The backend's cashCurrent already includes these, so we need to track them
    // to separate "pure cash" from "cash-like assets" for display purposes
    let cashLikeAssetsBefore = 0;
    const cashLikeDetails = {};  // Track by symbol to avoid duplicates across accounts

    // Process holdings from all accounts
    // Cash is spent first before MM assets, so MM only changes if cash change exceeds available cash
    Object.entries(allAccountHoldings || {}).forEach(([accountNumber, holdings]) => {
        if (!Array.isArray(holdings)) return;
        
        const acctInfo = accountCashInfo[accountNumber] || { cashBefore: 0, cashAfter: 0, cashChange: 0 };
        const cashChange = acctInfo.cashChange;  // negative = spent, positive = received
        
        // Find cash-like holdings in this account
        const cashLikeHoldings = holdings.filter(h => isCashLikeSymbol(h.Symbol || h.symbol));
        const totalCashLikeInAccount = cashLikeHoldings.reduce((sum, h) => 
            sum + (h['Holdings Current Value'] || h.holdingsCurrentValue || h.value || 0), 0);
        
        // Determine impact on MM assets:
        // - If spending (cashChange < 0): cash is used first, MM only touched if we spend more than cash available
        // - If receiving (cashChange > 0): cash increases, MM unchanged (assuming cash goes to cash account first)
        let mmImpact = 0;
        if (cashChange < 0) {
            // Spending money - cash is used first
            const cashBefore = acctInfo.cashBefore;
            const totalSpent = Math.abs(cashChange);
            // If we spent more than cash available, the excess comes from MM
            if (totalSpent > cashBefore) {
                mmImpact = -(totalSpent - cashBefore);  // negative = MM decreases
            }
            // Otherwise MM is unchanged (all spending covered by cash)
        }
        // If receiving cash (cashChange > 0), assume it goes to cash account, MM unchanged
        
        holdings.forEach(holding => {
            const symbol = holding.Symbol || holding.symbol;
            if (!isCashLikeSymbol(symbol)) return;
            
            const currentValue = holding['Holdings Current Value'] || holding.holdingsCurrentValue || holding.value || 0;
            if (currentValue <= 0) return;
            
            // Distribute the MM impact proportionally across cash-like holdings in this account
            const proportion = totalCashLikeInAccount > 0 ? currentValue / totalCashLikeInAccount : 0;
            const impact = mmImpact * proportion;
            const afterValue = Math.max(0, currentValue + impact);
            
            // Aggregate by symbol across all accounts
            if (!cashLikeDetails[symbol]) {
                cashLikeDetails[symbol] = {
                    name: symbol,
                    before: 0,
                    after: 0
                };
            }
            cashLikeDetails[symbol].before += currentValue;
            cashLikeDetails[symbol].after += afterValue;
            cashLikeAssetsBefore += currentValue;
        });
    });

    // Convert to array and calculate total after
    const cashLikeDetailsArray = Object.values(cashLikeDetails);
    const cashLikeAssetsAfter = cashLikeDetailsArray.reduce((sum, item) => sum + item.after, 0);

    // Sum margin debit balances across all accounts.
    // Prefer the backend-supplied marginCashUsed (explicitly set by all services).
    // Fall back to deriving from cashCurrent only when marginCashUsed is absent (null/undefined).
    const totalMarginUsed = accountAllocations.reduce(
        (sum, a) => {
            const explicit = a.marginCashUsed ?? a.margin_cash_used;
            if (explicit != null) return sum + explicit;
            const cash = a.cashCurrent ?? a.cash_current ?? 0;
            return sum + Math.max(0, -cash);
        },
        0
    );
    const hasMargin = totalMarginUsed > 0;

    // Backend's `cashCurrent` (= Cash Current Value) sums only UNASSIGNED cash-like
    // holdings. MM funds that are model-assigned (e.g. SPAXX in a sleeve) appear in
    // allAccountHoldings but are NOT included in cashCurrent.  If we naively subtract
    // the full holdings-derived cashLike from cashCurrent we can get a large negative
    // "pure cash" value.  Cap the displayed cash-like to the actual gross cash bucket
    // so the Cash row is never negative.
    const grossCashBefore = totalCashBefore + totalMarginUsed;
    const grossCashAfter  = totalCashAfter  + totalMarginUsed;
    const cappedCashLikeBefore = Math.min(cashLikeAssetsBefore, Math.max(0, grossCashBefore));
    const cappedCashLikeAfter  = Math.min(cashLikeAssetsAfter,  Math.max(0, grossCashAfter));

    // Backend's totalCashBefore/After includes cash-like assets, so we need to separate them
    // Pure cash = Total from backend - Cash-like assets found in holdings (capped above)
    // For margin accounts, cashCurrent is negative (= margin debt). We separate:
    //   displayCash  = totalCashBefore + totalMarginUsed  (shifts negative cash back to 0)
    //   displayMargin = -totalMarginUsed                  (shown as a separate red line)
    // So Total Liquid = pureCash + cashLike - margin = totalCash + cashLike ✓
    const pureCashBefore = grossCashBefore - cappedCashLikeBefore;
    const pureCashAfter  = grossCashAfter  - cappedCashLikeAfter;

    // Total liquid: Cash (excl margin) + Cash-like Assets - Margin Debt (unless margin is being ignored)
    const totalLiquidBefore = ignoreMargin
        ? pureCashBefore + cappedCashLikeBefore
        : pureCashBefore + cappedCashLikeBefore - totalMarginUsed;
    const totalLiquidAfter  = ignoreMargin
        ? pureCashAfter  + cappedCashLikeAfter
        : pureCashAfter  + cappedCashLikeAfter  - totalMarginUsed;

    // Calculate reserve status against total liquid (so ignoreMargin also affects reserve status)
    const reserveDeficitBefore = totalCashReserveTarget - totalLiquidBefore;
    const reserveDeficitAfter  = totalCashReserveTarget - totalLiquidAfter;

    // Determine status colors
    const getStatusColor = (deficit) => {
        if (deficit <= 0) return 'text-success'; // At or above target
        return 'text-danger'; // Below target
    };

    const getStatusIcon = (deficit) => {
        if (deficit <= 0) return 'bi-check-circle-fill';
        return 'bi-exclamation-triangle-fill';
    };

    return (
        <div className="card h-100">
            <div className="card-header bg-light py-2 px-3">
                <h6 className="mb-0">
                    <i className="bi bi-cash-stack me-2"></i>
                    Cash Summary
                </h6>
            </div>
            {isLoading ? (
                <div className="card-body py-4 px-3 d-flex justify-content-center align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>Loading cash data...</span>
                </div>
            ) : (
            <div className="card-body py-2 px-3">
                {/* Header Row */}
                <div className="row mb-2" style={{ fontSize: '0.7rem' }}>
                    <div className="col-6"></div>
                    <div className="col-3 text-end fw-bold text-muted">Before</div>
                    <div className="col-3 text-end fw-bold text-muted">After</div>
                </div>



                {/* Cash */}
                <div className="row align-items-center py-1 border-bottom" style={{ fontSize: '0.75rem' }}>
                    <div className="col-6">
                        <i className="bi bi-currency-dollar me-1 text-success"></i>
                        <span>Cash</span>
                    </div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(pureCashBefore)}</div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(pureCashAfter)}</div>
                </div>

                {/* Cash-like Assets */}
                <div className="row align-items-center py-1 border-bottom" style={{ fontSize: '0.75rem' }}>
                    <div className="col-6" title={
                        cashLikeDetailsArray.length > 0
                            ? `Money market funds: ${cashLikeDetailsArray.map(d => d.name).join(', ')}`
                            : `Money market funds like: ${DEFAULT_CASH_LIKE_SYMBOLS.join(', ')}`
                    }>
                        <i className="bi bi-bank me-1 text-info"></i>
                        <span style={{ cursor: 'help' }}>Cash-like Assets</span>
                    </div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(cappedCashLikeBefore)}</div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(cappedCashLikeAfter)}</div>
                </div>

                {/* Margin Debt — deducted from cash, shown before Total Liquid */}
                {hasMargin && (
                    <div
                        className="row align-items-center py-1 border-bottom"
                        style={{
                            fontSize: '0.75rem',
                            background: ignoreMargin ? 'rgba(108,117,125,0.06)' : 'rgba(220,53,69,0.06)',
                            opacity: ignoreMargin ? 0.5 : 1
                        }}
                        title={ignoreMargin
                            ? 'Margin debt is currently being ignored in Total Liquid and Reserve Status.'
                            : 'Borrowed margin balance. Shown separately from cash and deducted from Total Liquid.'}
                    >
                        <div className="col-6">
                            <i className={`bi bi-exclamation-triangle-fill me-1 ${ignoreMargin ? 'text-secondary' : 'text-danger'}`}></i>
                            <span style={{ cursor: 'help', textDecoration: ignoreMargin ? 'line-through' : 'none' }}>Margin Debt</span>
                            {ignoreMargin && <span className="ms-1 text-muted" style={{ fontSize: '0.65rem' }}>(ignored)</span>}
                        </div>
                        <div className={`col-3 text-end fw-bold ${ignoreMargin ? 'text-secondary' : 'text-danger'}`}>
                            -{formatCurrency(totalMarginUsed)}
                        </div>
                        <div className={`col-3 text-end fw-bold ${ignoreMargin ? 'text-secondary' : 'text-danger'}`}>
                            -{formatCurrency(totalMarginUsed)}
                        </div>
                    </div>
                )}

                {/* Total Liquid */}
                <div className="row align-items-center py-1 border-bottom" style={{ fontSize: '0.75rem' }}>
                    <div className="col-6">
                        <i className="bi bi-droplet-fill me-1 text-primary"></i>
                        <span className="fw-bold">Total Liquid</span>
                    </div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(totalLiquidBefore)}</div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(totalLiquidAfter)}</div>
                </div>

                {/* Reserve Target */}
                <div className="row align-items-center py-1 border-bottom" style={{ fontSize: '0.75rem' }}>
                    <div className="col-6">
                        <i className="bi bi-bullseye me-1 text-primary"></i>
                        <span>Reserve Target</span>
                    </div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(totalCashReserveTarget)}</div>
                    <div className="col-3 text-end fw-bold">{formatCurrency(totalCashReserveTarget)}</div>
                </div>

                {/* Reserve Status */}
                <div className="row align-items-center py-1" style={{ fontSize: '0.75rem' }}>
                    <div className="col-6">
                        <i className={`bi ${getStatusIcon(reserveDeficitBefore)} me-1 ${getStatusColor(reserveDeficitBefore)}`}></i>
                        <span>Reserve Status</span>
                    </div>
                    <div className={`col-3 text-end fw-bold ${getStatusColor(reserveDeficitBefore)}`}>
                        {reserveDeficitBefore <= 0 ? '+' : '-'}{formatCurrency(Math.abs(reserveDeficitBefore))}
                    </div>
                    <div className={`col-3 text-end fw-bold ${getStatusColor(reserveDeficitAfter)}`}>
                        {reserveDeficitAfter <= 0 ? '+' : '-'}{formatCurrency(Math.abs(reserveDeficitAfter))}
                    </div>
                </div>
            </div>
            )}
        </div>
    );
});

export default CashSummaryCard;
